import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';
import { useLocalStorageState } from 'shared_components/hooks/useLocalStorageState';

type PrimaryDiscographyRunContextType = {
  status:
    | 'not-started'
    | 'preparing'
    | 'running'
    | 'canceling'
    | 'cancelled'
    | 'finished'
    | 'failed'
    | null;
  actionsDisabled: boolean;
  cancelRun: () => Promise<void>;
  startRun: () => Promise<void>;
};

const PrimaryDiscographyRunContext = createContext<
  PrimaryDiscographyRunContextType | undefined
>(undefined);

const PrimaryDiscographyRunProviderImplementation = ({
  children,
  clientId,
}: {
  readonly children: React.ReactNode;
  readonly clientId: string;
}) => {
  const creatingRun = useRef(false);
  const [status, setStatus] = useLocalStorageState<
    PrimaryDiscographyRunContextType['status']
  >('primary_discography_run_status', null, {
    namespace: clientId,
  });

  const { adminApi: api } = useApi();

  const getLatestPipelineStatus = useCallback(async (clientId: string) => {
    return api
      .retrievePipelineRunStatus({
        userId: clientId,
      })
      .then((data) => {
        switch (data.status) {
          case 'QUEUED':
          case 'NOT_STARTED':
          case 'MANAGED':
          case 'STARTING': {
            setStatus('preparing');
            break;
          }
          case 'STARTED': {
            setStatus('running');
            break;
          }
          case 'SUCCESS': {
            setStatus('finished');
            break;
          }
          case 'CANCELING': {
            setStatus('canceling');
            break;
          }
          case 'CANCELED': {
            setStatus('cancelled');
            break;
          }
          case 'FAILURE': {
            setStatus('failed');
            break;
          }
          default: {
            setStatus('not-started');
            break;
          }
        }
      });
  }, []);

  useEffect(() => {
    if (creatingRun.current) return;
    if (
      status !== 'preparing' &&
      status !== 'running' &&
      status !== 'canceling'
    ) {
      getLatestPipelineStatus(clientId).catch((err) => {
        console.log(err);
        setStatus((state) =>
          state !== 'running' && state !== 'preparing' && state !== 'canceling'
            ? 'not-started'
            : state
        );
      });
    }
  }, [clientId, status]);

  useEffect(() => {
    if (creatingRun.current) return;
    if (
      status !== 'running' &&
      status !== 'preparing' &&
      status !== 'canceling'
    )
      return;

    let finalJobStatusFetchTimeout: ReturnType<typeof setTimeout> | null = null;

    const fetchFinalJobStatus = () => {
      getLatestPipelineStatus(clientId).catch((err) => {
        console.log(err);
        setStatus('not-started');
      });

      finalJobStatusFetchTimeout = setTimeout(() => {
        fetchFinalJobStatus();
      }, 3000);
    };

    finalJobStatusFetchTimeout = setTimeout(() => {
      fetchFinalJobStatus();
    }, 3000);

    return () => {
      if (finalJobStatusFetchTimeout) {
        clearTimeout(finalJobStatusFetchTimeout);
      }
    };
  }, [status, clientId]);

  useEffect(() => {
    if (creatingRun.current) return;
    getLatestPipelineStatus(clientId).catch((err) => {
      console.log(err);
      setStatus((state) =>
        state !== 'running' && state !== 'preparing' && state !== 'canceling'
          ? 'not-started'
          : state
      );
    });
  }, [clientId]);

  const startRun = useCallback(async () => {
    setStatus('preparing');
    creatingRun.current = true;
    await api
      .createPipelineRun({ pipelineRunRequest: { userId: clientId } })
      .then(() =>
        getLatestPipelineStatus(clientId).catch((err) => {
          console.log(err);
          setStatus((state) =>
            state !== 'running' &&
            state !== 'preparing' &&
            status !== 'canceling'
              ? 'not-started'
              : state
          );
        })
      )
      .catch(() => {
        setStatus((state) =>
          state !== 'running' && state !== 'preparing' && status !== 'canceling'
            ? 'not-started'
            : state
        );
      })
      .finally(() => {
        creatingRun.current = false;
      });
  }, []);

  const cancelRun = useCallback(async () => {
    setStatus('canceling');

    api
      .cancelPrimaryDiscographyRunRemove({
        userId: clientId,
      })
      .then(() =>
        getLatestPipelineStatus(clientId).catch((err) => {
          console.log(err);
          setStatus((state) =>
            state !== 'running' &&
            state !== 'preparing' &&
            status !== 'canceling'
              ? 'not-started'
              : state
          );
        })
      )
      .catch((ex) => {
        console.log(ex);
      });
  }, []);

  const actionsDisabled = useMemo<boolean>(() => {
    switch (status) {
      case 'not-started':
      case 'finished':
      case 'cancelled':
      case 'failed': {
        return false;
      }
      default: {
        return true;
      }
    }
  }, [status]);

  const value = useMemo(() => {
    return {
      status,
      actionsDisabled,
      startRun,
      cancelRun,
    };
  }, [status, startRun, cancelRun]);

  return (
    <PrimaryDiscographyRunContext.Provider value={value}>
      {children}
    </PrimaryDiscographyRunContext.Provider>
  );
};

export const PrimaryDiscographyRunProvider = (
  props: Omit<
    ComponentProps<typeof PrimaryDiscographyRunProviderImplementation>,
    'clientId'
  >
) => {
  const { clientId } = useClientId();
  if (!clientId) return null;

  return (
    <PrimaryDiscographyRunProviderImplementation
      {...props}
      clientId={clientId}
    />
  );
};

export const usePrimaryDiscographyRun = () => {
  const context = useContext(PrimaryDiscographyRunContext);

  if (!context)
    throw new Error('Cannot use PrimaryDiscographyRunContext without provider');

  return context;
};
